import type { ProjectTrack, TrackItem, TrackItemDependency } from '../../api/types';

/**
 * Pure layout model for the static read Gantt — NO React, NO SVG. The view
 * (GanttChart.tsx) renders whatever this produces, and T6 unit-tests the rules
 * here directly. Two rules from the design carry the weight:
 *
 *  - Summary actual-% reads `track.currentUnits` (the authoritative total), NEVER
 *    sum(items). `track.currentUnits >= sum(item.currentUnits)` always, because a
 *    track-only log increments the track but no item. So when a track has progress
 *    but its items don't (sum is 0), the item sub-bars are "unattributed" (greyed)
 *    and the real % still shows on the summary bar — never a false 0%.
 *  - Bar dates resolve `scheduled_* ?? planned_*`. In PR1 scheduled_* is always null
 *    (engine lands in PR2), so bars draw from the plan; once the engine is wired the
 *    same model renders the computed schedule with no view change. An item is
 *    "unplanned" until BOTH dates resolve — unplanned items get no bar (greyed row).
 */

/** A bar as day-offsets from the model's range start. `x1` is exclusive (end day + 1). */
export interface GanttBar {
  x0: number;
  x1: number;
}

/**
 * Pace signal (W3): where the fill stands against where today sits in the bar.
 * `late` comes from the derived status; `behind` means actual % lags the
 * expected % at today by more than BEHIND_THRESHOLD_PCT points.
 */
export type Pace = 'onpace' | 'behind' | 'late';

export const BEHIND_THRESHOLD_PCT = 10;

export interface GanttTrackRow {
  kind: 'track';
  rowIndex: number;
  id: string;
  label: string;
  type: string;
  status: string;
  /** Actual-% — the backend roll-up (weighted sub-task progress), else the track counter. */
  actualPct: number;
  /** True when the track has progress but none of its items do (greyed sub-bars below). */
  unattributed: boolean;
  /**
   * Summary span = min(item start) .. max(item end) across planned items;
   * falls back to the track's own planned dates (W3) when no item is dated;
   * null when neither exists.
   */
  bar: GanttBar | null;
  /** Expected % at today from the bar span (0 before start, 100 after end); null when no bar. */
  expectedPct: number | null;
  pace: Pace;
}

export interface GanttItemRow {
  kind: 'item';
  rowIndex: number;
  id: string;
  trackId: string;
  name: string;
  status: string;
  /** Item actual-% from item.currentUnits/totalUnits. */
  actualPct: number;
  /** Both resolved dates present → schedulable/drawable. */
  planned: boolean;
  /** Greyed because the parent track's progress isn't item-attributed. */
  unattributed: boolean;
  /** On the critical path (PR2 feeds this; empty in PR1). */
  critical: boolean;
  /** Live bar (scheduled ?? planned); null when unplanned. */
  bar: GanttBar | null;
  /** Frozen baseline ghost bar; null until a baseline is set. */
  baselineBar: GanttBar | null;
  /** Expected % at today from the bar span (0 before start, 100 after end); null when no bar. */
  expectedPct: number | null;
  pace: Pace;
}

/** Input milestone for the Gantt (mapped from a delivery milestone). */
export interface GanttMilestoneInput {
  id: string;
  name: string;
  /** Effective ISO date to PLACE the diamond at — scheduled_date ?? due_date. */
  date: string;
  done?: boolean;
  /** The manually-typed due date, when it differs from `date` (link-driven move). */
  dueDate?: string | null;
  /** On-record note for why a date moved; surfaced on hover. */
  delayReason?: string | null;
}

export interface GanttMilestoneRow {
  kind: 'milestone';
  rowIndex: number;
  id: string;
  name: string;
  /** x day-offset of the (effective) milestone date from range start; null when out of range. */
  dayOffset: number | null;
  done: boolean;
  /** Set when the engine moved the milestone off its typed due date (graph wins). */
  movedFromDueDate: string | null;
  delayReason: string | null;
}

export type GanttRow = GanttTrackRow | GanttItemRow | GanttMilestoneRow;

export interface GanttArrow {
  id: string;
  fromItemId: string;
  toItemId: string;
  lagDays: number;
}

/** A polymorphic schedule edge whose endpoints may be items OR milestones. */
export interface GanttScheduleLinkInput {
  id: string;
  predecessorKind: 'item' | 'milestone';
  predecessorId: string;
  successorKind: 'item' | 'milestone';
  successorId: string;
  lagDays: number;
}

export interface GanttMilestoneArrow {
  id: string;
  from: { kind: 'item' | 'milestone'; id: string };
  to: { kind: 'item' | 'milestone'; id: string };
  lagDays: number;
}

export interface GanttModel {
  rows: GanttRow[];
  arrows: GanttArrow[];
  /** Milestone↔node edges, kept only when both endpoints are placeable. */
  milestoneArrows: GanttMilestoneArrow[];
  /** Total span in days (>= 1); the view divides the x-axis by this. */
  totalDays: number;
  /** Range start as a day-number (epoch days); null when empty. */
  rangeStartDay: number | null;
  /** "Today" as a day-offset from rangeStart, or null if outside the range. */
  todayOffset: number | null;
  /** No planned items anywhere → render the empty state. */
  empty: boolean;
}

const MS_PER_DAY = 86_400_000;

/** ISO date (YYYY-MM-DD) → epoch day-number (UTC midnight), or null. */
export function toDayNumber(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / MS_PER_DAY);
}

function pct(current: number, total: number): number {
  if (!total || total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((current / total) * 100)));
}

const resolveStart = (i: TrackItem) => i.scheduledStart ?? i.plannedStart ?? null;
const resolveEnd = (i: TrackItem) => i.scheduledEnd ?? i.plannedEnd ?? null;

/** Today as an epoch day-number. Injectable for deterministic tests. */
export function todayDayNumber(now: number = Date.now()): number {
  return Math.floor(now / MS_PER_DAY);
}

export function buildGanttModel(
  tracks: ProjectTrack[],
  dependencies: TrackItemDependency[] = [],
  criticalPath: string[] = [],
  now?: number,
  milestones: GanttMilestoneInput[] = [],
  milestoneLinks: GanttScheduleLinkInput[] = [],
): GanttModel {
  const criticalSet = new Set(criticalPath);

  // Pass 1 — collect every candidate day-number to size the axis (live +
  // baseline + track-level target spans, W3).
  const days: number[] = [];
  for (const track of tracks) {
    const ts = toDayNumber(track.plannedStart);
    const te = toDayNumber(track.plannedEnd);
    if (ts !== null) days.push(ts);
    if (te !== null) days.push(te);
    for (const item of track.items ?? []) {
      const s = toDayNumber(resolveStart(item));
      const e = toDayNumber(resolveEnd(item));
      if (s !== null) days.push(s);
      if (e !== null) days.push(e);
      const bs = toDayNumber(item.baselineStart);
      const be = toDayNumber(item.baselineEnd);
      if (bs !== null) days.push(bs);
      if (be !== null) days.push(be);
    }
  }

  for (const m of milestones) {
    const d = toDayNumber(m.date);
    if (d !== null) days.push(d);
  }

  const empty = days.length === 0;
  const rangeStartDay = empty ? null : Math.min(...days);
  const rangeEndDay = empty ? null : Math.max(...days);
  const totalDays = rangeStartDay === null || rangeEndDay === null ? 1 : rangeEndDay - rangeStartDay + 1;

  const bar = (startIso: string | null, endIso: string | null): GanttBar | null => {
    const s = toDayNumber(startIso);
    const e = toDayNumber(endIso);
    if (s === null || e === null || rangeStartDay === null) return null;
    return { x0: s - rangeStartDay, x1: e - rangeStartDay + 1 }; // inclusive end → +1
  };

  const rows: GanttRow[] = [];
  let rowIndex = 0;

  // Milestones interleaved chronologically among the phase rows: a milestone is
  // emitted just before the first phase that starts on/after its date (so PO/DP
  // land before Procurement, FAT right after the FAT phase, handover at the end).
  const datedMilestones = milestones
    .map((m) => ({ m, d: toDayNumber(m.date) }))
    .filter((x): x is { m: GanttMilestoneInput; d: number } => x.d !== null)
    .sort((a, b) => a.d - b.d);
  let msIdx = 0;
  const emitMilestone = ({ m, d }: { m: GanttMilestoneInput; d: number }) => {
    rows.push({
      kind: 'milestone',
      rowIndex: rowIndex++,
      id: m.id,
      name: m.name,
      dayOffset: rangeStartDay !== null ? d - rangeStartDay : null,
      done: !!m.done,
      // "graph wins": the diamond sits at `date`; flag when that diverges from the
      // typed due date so the view can show "moved by link".
      movedFromDueDate: m.dueDate && m.dueDate !== m.date ? m.dueDate : null,
      delayReason: m.delayReason ?? null,
    });
  };

  const today = todayDayNumber(now);

  /** Expected % at today within a bar's inclusive day span; null when no bar. */
  const expectedFor = (b: GanttBar | null): number | null => {
    if (b === null || rangeStartDay === null) return null;
    const startDay = rangeStartDay + b.x0;
    const endDay = rangeStartDay + b.x1 - 1; // x1 is exclusive
    if (today < startDay) return 0;
    if (today > endDay) return 100;
    return Math.round(((today - startDay + 1) / (endDay - startDay + 1)) * 100);
  };

  const paceFor = (status: string, actualPct: number, expectedPct: number | null): Pace => {
    if (status === 'late') return 'late';
    if (expectedPct !== null && actualPct < expectedPct - BEHIND_THRESHOLD_PCT) return 'behind';
    return 'onpace';
  };

  // Order tracks chronologically (earliest planned/scheduled start first) so the
  // chart reads top-to-bottom as the project unfolds — a lifecycle cascade like
  // PO → Invoice → Purchasing → … → Delivery → Qualification. Tracks with no
  // dated item sort last, keeping their incoming order.
  const trackStart = (t: ProjectTrack): number => {
    const starts = (t.items ?? [])
      .map((i) => toDayNumber(resolveStart(i)))
      .filter((d): d is number => d !== null);
    return starts.length ? Math.min(...starts) : Number.POSITIVE_INFINITY;
  };
  const orderedTracks = tracks
    .map((t, i) => ({ t, i }))
    .sort((a, b) => trackStart(a.t) - trackStart(b.t) || a.i - b.i)
    .map((x) => x.t);

  for (const track of orderedTracks) {
    // Emit milestones due on/before this phase's start (chronological interleave).
    const phaseStart = trackStart(track);
    while (msIdx < datedMilestones.length && datedMilestones[msIdx].d <= phaseStart) {
      emitMilestone(datedMilestones[msIdx]);
      msIdx++;
    }

    const items = [...(track.items ?? [])].sort((a, b) => a.sequence - b.sequence);

    const itemUnitsLogged = items.reduce((s, i) => s + (i.currentUnits ?? 0), 0);
    const unattributed = track.currentUnits > 0 && itemUnitsLogged === 0;

    // Summary span across planned items; falls back to the track's own
    // planned dates (W3) so a track with no dated items still gets a bar.
    const plannedStarts = items.map((i) => toDayNumber(resolveStart(i))).filter((d): d is number => d !== null);
    const plannedEnds = items.map((i) => toDayNumber(resolveEnd(i))).filter((d): d is number => d !== null);
    const summaryBar =
      plannedStarts.length && plannedEnds.length && rangeStartDay !== null
        ? { x0: Math.min(...plannedStarts) - rangeStartDay, x1: Math.max(...plannedEnds) - rangeStartDay + 1 }
        : bar(track.plannedStart ?? null, track.plannedEnd ?? null);

    const trackStatus = track.computedStatus ?? track.status;
    // Prefer the backend roll-up (weighted by sub-task size) so the summary bar
    // matches the headline %; fall back to the track's own counter.
    const trackActualPct = track.progressPct ?? pct(track.currentUnits, track.totalUnits);
    const trackExpected = expectedFor(summaryBar);

    rows.push({
      kind: 'track',
      rowIndex: rowIndex++,
      id: track.id,
      label: track.label,
      type: track.type,
      status: trackStatus,
      actualPct: trackActualPct,
      unattributed,
      bar: summaryBar,
      expectedPct: trackExpected,
      pace: paceFor(trackStatus, trackActualPct, trackExpected),
    });

    for (const item of items) {
      const liveBar = bar(resolveStart(item), resolveEnd(item));
      const itemStatus = item.computedStatus ?? item.status ?? '';
      const itemActualPct = item.progressPct ?? pct(item.currentUnits, item.totalUnits);
      const itemExpected = expectedFor(liveBar);
      rows.push({
        kind: 'item',
        rowIndex: rowIndex++,
        id: item.id,
        trackId: track.id,
        name: item.name,
        status: itemStatus,
        actualPct: itemActualPct,
        planned: liveBar !== null,
        unattributed,
        critical: criticalSet.has(item.id),
        bar: liveBar,
        baselineBar: bar(item.baselineStart ?? null, item.baselineEnd ?? null),
        expectedPct: itemExpected,
        pace: paceFor(itemStatus, itemActualPct, itemExpected),
      });
    }
  }

  // Milestones dated after the last phase start (e.g. final handover) close out.
  while (msIdx < datedMilestones.length) {
    emitMilestone(datedMilestones[msIdx]);
    msIdx++;
  }

  // Only keep arrows whose BOTH endpoints have a drawable bar.
  const drawable = new Set(
    rows.filter((r): r is GanttItemRow => r.kind === 'item' && r.bar !== null).map((r) => r.id),
  );
  const arrows: GanttArrow[] = dependencies
    .filter((d) => drawable.has(d.predecessorId) && drawable.has(d.successorId))
    .map((d) => ({ id: d.id, fromItemId: d.predecessorId, toItemId: d.successorId, lagDays: d.lagDays }));

  // Milestone↔node edges: a milestone endpoint is placeable when its diamond is in
  // range; an item endpoint when it has a drawable bar.
  const placeableMilestones = new Set(
    rows
      .filter((r): r is GanttMilestoneRow => r.kind === 'milestone' && r.dayOffset !== null)
      .map((r) => r.id),
  );
  const placeable = (kind: 'item' | 'milestone', id: string) =>
    kind === 'item' ? drawable.has(id) : placeableMilestones.has(id);
  const milestoneArrows: GanttMilestoneArrow[] = milestoneLinks
    .filter(
      (l) =>
        placeable(l.predecessorKind, l.predecessorId) &&
        placeable(l.successorKind, l.successorId),
    )
    .map((l) => ({
      id: l.id,
      from: { kind: l.predecessorKind, id: l.predecessorId },
      to: { kind: l.successorKind, id: l.successorId },
      lagDays: l.lagDays,
    }));

  const todayOffset =
    rangeStartDay !== null && today >= rangeStartDay && today <= (rangeEndDay ?? rangeStartDay)
      ? today - rangeStartDay
      : null;

  return { rows, arrows, milestoneArrows, totalDays, rangeStartDay, todayOffset, empty };
}
